import type { RefObject } from 'react'

import { noop } from 'lodash'

import { type IModalProps, Modal } from '../Modal'
import { useReturnFocus } from '../ModalDialog/useReturnFocus'
import {
  ConfirmationForm,
  type IConfirmationFormProps,
} from './components/ConfirmationForm'
import { ConfirmationModalStyled } from './styles/ConfirmationModalStyled'
import { splitDataAttributes } from '@ui/core/dataAttributes'
import { type DataAttributes } from '@ui/core/types'
import { type IconElement, IconQuestion, renderIcon } from '@ui/elements/Icon'
import { Space } from '@ui/elements/Space'

export interface IConfirmationModalProps
  extends Omit<IConfirmationFormProps, 'handleConfirm'>, DataAttributes {
  icon?: IconElement | null
  open?: boolean
  /** The text of the confirmation modal */
  header?: string
  maxWidth?: IModalProps['maxWidth']
  onConfirm?: IConfirmationFormProps['handleConfirm']
  handleDismiss?: () => void
  /**
   * Where focus goes when the modal closes. Pass it when the control that
   * opened the modal is gone by then: a menu item, or a row the confirmed
   * action removes. Focus moves without scrolling, and only if the element is
   * still in the document.
   *
   * Without it, focus returns to the element that had focus when the modal
   * opened. Pass it from the first render: whether the modal uses it is fixed
   * when the modal mounts. The ref itself may change, and the one passed when
   * the modal closes is used.
   *
   * A hand-off keeps focus with the next dialog: when a ModalDialog or
   * ConfirmationModal opens in the same update as this modal closes, focus
   * is left for it instead of passing through this element first.
   */
  returnFocusRef?: RefObject<HTMLElement>
}

export const ConfirmationModal = ({
  open,
  type,
  icon = <IconQuestion />,
  header = 'Confirmation',
  maxWidth,
  handleDismiss,
  onConfirm = noop,
  returnFocusRef,
  ...otherProps
}: IConfirmationModalProps) => {
  const { dataAttributes, otherProps: otherConfirmationFormProps } =
    splitDataAttributes(otherProps)
  const disableRestoreFocus = useReturnFocus(open, returnFocusRef)

  return (
    <Modal
      aria-labelledby='confirmation-modal-title'
      open={open}
      maxWidth={maxWidth}
      onClose={handleDismiss}
      disableRestoreFocus={disableRestoreFocus}
      {...dataAttributes}
    >
      <Modal.Card>
        <ConfirmationModalStyled>
          <Modal.Header onClose={handleDismiss}>
            {icon ? (
              <Space direction='horizontal' size='small' align='center'>
                {renderIcon(icon, { color: type, fontSize: 24 })}
                <span id='confirmation-modal-title'>{header}</span>
              </Space>
            ) : (
              <span id='confirmation-modal-title'>{header}</span>
            )}
          </Modal.Header>
          <Modal.Content>
            <ConfirmationForm
              type={type}
              handleDismiss={handleDismiss}
              handleConfirm={onConfirm}
              {...otherConfirmationFormProps}
            />
          </Modal.Content>
        </ConfirmationModalStyled>
      </Modal.Card>
    </Modal>
  )
}

ConfirmationModal.displayName = 'ConfirmationModal'
